/**
 * Просмотр приложенной записи экрана в карточке задачи.
 *
 * Ссылка «на просмотр» запрашивается по нажатию, а не заранее, как у
 * картинок: превью у видео нет, а сотни мегабайт браузер начнёт тянуть,
 * только когда запись откроют. Перемотку даёт поддержка Range на сервере.
 */

import { Play, VideoOff } from "lucide-react";
import { useState } from "react";

import { resolveApiUrl } from "@/shared/api/http";
import { Button, Modal } from "@/shared/ui";
import { Download } from "@/shared/ui/motionIcons";

import { type Attachment, useAttachmentOpenUrlMutation } from "@taskflow/client-core/tasks";

export function AttachmentVideo({
  file,
  onDownload,
}: {
  file: Attachment;
  onDownload: () => void;
}) {
  const [openUrl, link] = useAttachmentOpenUrlMutation();
  const [viewing, setViewing] = useState(false);
  const [broken, setBroken] = useState(false);

  const open = () => {
    setBroken(false);
    setViewing(true);
    // Ссылка живёт минуты — на каждое открытие новая.
    void openUrl({ id: file.id });
  };

  const url = link.data === undefined || link.isLoading ? null : resolveApiUrl(link.data.url);
  const failed = broken || link.isError || (link.isSuccess && url === null);

  return (
    <>
      <button
        type="button"
        className="flex size-10 shrink-0 items-center justify-center rounded border border-line bg-sunken text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        aria-label={`Просмотреть ${file.filename}`}
        title="Просмотреть запись"
        onClick={open}
      >
        <Play className="size-4" aria-hidden />
      </button>

      <Modal
        open={viewing}
        onClose={() => setViewing(false)}
        title={file.filename}
        width="xl"
        footer={
          <Button size="sm" variant="secondary" icon={Download} onClick={onDownload}>
            Скачать
          </Button>
        }
      >
        {failed ? (
          <p className="flex items-center justify-center gap-2 py-10 text-[13px] text-faint">
            <VideoOff className="size-4" aria-hidden />
            Не удалось загрузить запись
          </p>
        ) : url === null ? (
          <p className="py-10 text-center text-[13px] text-faint">Загрузка…</p>
        ) : (
          <video
            src={url}
            controls
            autoPlay
            className="mx-auto block max-h-[calc(100dvh-12rem)] max-w-full bg-black"
            onError={() => setBroken(true)}
          />
        )}
      </Modal>
    </>
  );
}
